import React, { useContext, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    CollapsibleList,
    DateTimeUtils,
    DYLAvatar,
    GroupedTimeLine,
    LinkedAccount,
    Notification,
    Person,
    STATUS_TYPES,
    Table,
    TableLoader,
    Contact
} from "dyl-components";

import { Dropdown, Grid, Header, Icon } from "semantic-ui-react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { PhoneUtil, StringUtils } from "utils";
import customerTabActions from "actions/customer_tab";

import CustomerPipelineSubrowHeader from "./CustomerPipelinesSubrowHeader";
import CustomerPipelinesSubrowColumns from "./CustomerPipelineSubrowColumns";

import { ContactContext } from "shared/context/ContactProvider";
import customerPipelineActions from "actions/customer_pipeline";
import accountActions from "actions/account";
import RestrictedView from "shared/RestrictedView";
import { USER_PERMISSIONS } from "dyl-components";
import { ActivityPopup } from "shared/forms/NewActivity";
import TextIcon from "shared/TextIcon";

const TableCellComponent =
    (outcome) =>
    ({ children, allowDisable = true }) => {
        const isClosed = outcome === "closed";
        return (
            <Table.Cell {...(allowDisable ? { disabled: isClosed } : {})}>
                {children}
            </Table.Cell>
        );
    };

const SubrowContent = ({ logs }) => {
    const getIcon = (entry) => {
        if (entry.action_type === "pipeline") {
            switch (entry.action) {
                case "created":
                    return "fas fa-square-user";
                case "stage_changed":
                case "changed":
                case "closed":
                case "assigned":
                    return "fas fa-funnel-dollar";
                default:
                    return "fas fa-clipboard";
            }
        }
        if (entry.action_type === "sequence") {
            switch (entry.action) {
                case "applied":
                case "changed":
                case "removed":
                    return "fas fa-sitemap";
                default:
                    return "fas fa-clipboard";
            }
        }
        if (entry.action_type === "contact_association") {
            switch (entry.action) {
                case "changed":
                    return "fas fa-image-user";
                default:
                    return "fas fa-clipboard";
            }
        }
        return "fas fa-clipboard";
    };

    return (
        <Table.CollapsibleRowContent>
            <Table.Cell width={1} />
            <Table.Cell colSpan={12}>
                <div className="HistorySection">
                    <GroupedTimeLine
                        headers={<CustomerPipelineSubrowHeader />}
                        isLoading={false}
                        additionalColumnNumber={3}
                        useDateFunctions={false}
                        groups={DateTimeUtils.groupDates(
                            (logs || []).map((log) => {
                                const [messageType,] = log?.message.split(" ");
                                const isSystemGenerated = messageType?.includes("System");
                                return {
                                    ts: log.activity,
                                    icon: <Icon className={getIcon(log)} />,
                                    columns: (
                                        <CustomerPipelinesSubrowColumns
                                            activityHeader={StringUtils.toSentenceCase(
                                                (log.action_type || "").split("_").join(" ")
                                            )}
                                            activitySubheader={log.action}
                                            messageNotes={
                                                <div>
                                                    {log.message}
                                                    {((log.action === "changed" &&
                                                        log.action_type ===
                                                            "pipeline") ||
                                                        log.action ===
                                                            "closed") && (
                                                        <CollapsibleList
                                                            minimumNumberOfItemsToShow={
                                                                0
                                                            }
                                                            items={[
                                                                "",
                                                                "",
                                                                `${
                                                                    log.close_reason
                                                                        ? `${
                                                                            log.close_reason
                                                                        }${
                                                                            log.close_type
                                                                                ? `/${log.close_type}`
                                                                                : ""
                                                                        }`
                                                                        : ""
                                                                }`,
                                                                "",
                                                                log.note || "",
                                                            ]}
                                                            action="View"
                                                        />
                                                    )}
                                                </div>
                                            }
                                            user={!isSystemGenerated ? (
                                                log.user && <DYLAvatar
                                                    name={log.user.username}
                                                    round
                                                    size='3em'
                                                    maxInitials={2}
                                                    inactive={!log.user.active}
                                                />
                                            ) : (
                                                <Icon
                                                    className='fas fa-robot AutomationIcon'
                                                    circular
                                                    size='large' />
                                            )}
                                        />
                                    ),
                                };
                            })
                        )}
                    />
                </div>
            </Table.Cell>
        </Table.CollapsibleRowContent>
    );
};

const CustomerPipelinesRow = ({ pipeline }) => {
    const {
        contact,
        status,
        pipeline_category_name,
        pipeline_stage_name,
        pipeline_stage_id,
        pipeline_stage_sequence_id,
        id,
    } = pipeline;

    const { isReading: isReadingTimeline, logs } = useSelector(
        (state) =>
            state.customer_tab.pipelineTimelines[id] || {
                isReading: false,
                logs: [],
            }
    );
    const  account = useSelector(state => state.account?.account)

    const { onOpenModal } = useContext(ContactContext);

    const [collapsed, setCollapsed] = useState(true);

    const dispatch = useDispatch();

    const refreshTimeline = async () => {
        dispatch(customerTabActions.readPipelineTimeline(pipeline.id));
    };

    const onToggle = () => {
        setCollapsed(!collapsed);
        if (collapsed) {
            refreshTimeline();
        }
    };

    const TableCell = useMemo(() => TableCellComponent(status || ""), [status]);

    const isClosed = status === "closed";

    const contactDropdownText = useMemo(() => (
        <LinkedAccount
            account={`${contact?.first_name} ${contact?.last_name}`}
            maxWidth={'8.25em'}
            subtitle={contact?.email?.email}
            popup
            linkToAccount={
                <RestrictedView type="link" userPermission={USER_PERMISSIONS.CONTACTS_VIEW} userRestrict assignmentIds={[(contact?.assignees?.owner_id || 0), (contact?.assignees?.coowner_id || 0)]}>
                    <Link onClick={e => { e.stopPropagation(); }} target={'_blank'} to={`/contact/${contact?.id}`}>{contact?.first_name} {contact?.last_name}</Link>
                </RestrictedView>
            }
        />
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [contact?.first_name, contact?.last_name, contact?.email?.email, contact?.id]);

    const rawContactsForPinning = useSelector(state => state.account.contactsForPinning);
    const isReadingContactsForPinning = useSelector(state => state.account.isReadingContactsForPinning);

    const contactsForPinning = useMemo(() => rawContactsForPinning.map(c => {
        const name = `${c.first_name} ${c.last_name}`;
        return {
            key: c.id,
            value: c.id,
            text: name,
            disabled: Boolean(c.module_summary?.account_stage),
            content: <Person username={name} email={c?.email?.email || ''} />
        };
    }), [rawContactsForPinning]);

    const filteredContactOptions = useMemo(() =>
        !isClosed ? contactsForPinning.filter(({ value }) => value !== contact?.id) : [],
        [isClosed, contactsForPinning, contact?.id]
    );

    const { account_id } = useParams();

    const [params] = useSearchParams();

    const onTransferPipeline = async (person_id) => {
        try {
            await dispatch(customerPipelineActions.update(Number(account_id), {
                pipeline_stage_id,
                person_id,
                pipeline_id: id,
                sequence_id: pipeline_stage_sequence_id || null
            }, { action: 'transfer' }));
            Notification.alert('Successfully transferred pipeline!', STATUS_TYPES.SUCCESS);
            dispatch(customerTabActions.readPipelines(Number(account_id), {
                page: 1,
                limit: 10,
                ...Object.fromEntries(params)
            }));
            dispatch(accountActions.readContactsForPinning(account_id, { page: 1, limit: 100 }));
        } catch (e) {
            console.log(e);
            Notification.alert('Failed to transfer pipeline', STATUS_TYPES.ERROR);
        }
    }

    const { userPhoneCreds, hasEmailIntegrations } = useSelector(state => ({
        userPhoneCreds: state?.officeView?.creds,
        hasEmailIntegrations: state.oauth_integrations?.integrations?.length > 0
    }));

    const isPhoneSystemSupported = PhoneUtil.isPhoneSystemSupported(userPhoneCreds);

    const onClickToDial = (contact) => {
        const { id: account_id } = account || {};
        const { id: contact_id } = contact || {};
        PhoneUtil.C2D({ payload: { contact_id, account_id, destination: contact?.phone?.phone }, dispatch });
    }

    return (
        <>
            <Table.CollapsibleRow
                onToggle={onToggle}
                collapsed={collapsed}
                toggleDisabled={!Boolean(pipeline.id)}
                subrowContent={
                    isReadingTimeline ? (
                        <TableLoader colspan={10} isLoading />
                    ) : (
                        <SubrowContent logs={logs} />
                    )
                }
            >
                <TableCell>
                    <div>
                        <b>
                            {DateTimeUtils.formatEpoch(
                                pipeline.created,
                                DateTimeUtils.WORD_DATE_FORMAT
                            )}
                        </b>
                    </div>
                    <div>
                        {DateTimeUtils.formatEpoch(
                            pipeline.created,
                            DateTimeUtils.TIME_FORMAT
                        )}
                    </div>
                </TableCell>
                <TableCell allowDisable={false}>
                    <Grid verticalAlign="middle" className="PipelineRow__contact" columns={"equal"}>
                        <Grid.Column>
                            <Contact
                                leftSideContent={
                                    <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.ACCOUNT_EDIT} userRestrict assignmentIds={[(account?.assignees?.owner_id || 0), (account?.assignees?.coowner_id || 0)]}>
                                        <Dropdown
                                            placeholder='Select Primary'
                                            selectOnBlur={true}
                                            selection
                                            options={filteredContactOptions}
                                            {...(isClosed ? { icon: null } : {})}
                                            value={contact?.id}
                                            onChange={(_, { value: contact_id }) => {onTransferPipeline(contact_id, contact?.id)}}
                                            compact
                                            text={contactDropdownText}
                                            loading={isReadingContactsForPinning}
                                            className="PipelineRow__contact-dropdown"
                                        />
                                    </RestrictedView>
                                }
                                isFixedWidth={false}
                                phone={contact?.phone?.phone}
                                email={contact?.email?.email}
                                account_id={account_id}
                                contact_id={id}
                                isPhoneSystemSupported={isPhoneSystemSupported}
                                hasEmailIntegrations={hasEmailIntegrations}
                                onCallClick={() => onClickToDial(contact)}
                                onTextClick={() => onOpenModal('text', contact?.id)}
                                onEmailClick={() => onOpenModal('email', contact?.id)}
                                onNoteClick={() => onOpenModal('note', contact)}
                                ActivityPopup={ActivityPopup}
                                TextIcon={TextIcon}
                                callActivityProps={{
                                    hideActivitySelection: true,
                                    contact: { id: contact?.id },
                                    isContact: true
                                }}
                                emailActivityProps={{
                                    readTimeline: () => { },
                                    contact,
                                    isContact: true,
                                    emailOnly: true
                                }}
                                callSuspended={contact?.call_suspended}
                            />
                        </Grid.Column>
                    </Grid>
                </TableCell>
                <TableCell>
                    {status && (
                        <span
                            className={`ModuleRow__outcome--${
                                isClosed ? "closed" : "open"
                            }`}
                        >
                            <b>{StringUtils.capitalize(status)}</b>
                        </span>
                    )}
                </TableCell>
                <TableCell>
                    <Header as="h5" disabled={isClosed}>
                        {pipeline_category_name}
                        <Header.Subheader>
                            {pipeline_stage_name}
                        </Header.Subheader>
                    </Header>
                </TableCell>
            </Table.CollapsibleRow>
        </>
    );
};

export default CustomerPipelinesRow;
